import Loading from '@doordash/component-loading'
import Separator from '@doordash/component-separator'
import { Inset, Spacing, StackChildren, Text } from '@doordash/design-language'
import { JSONSchemaType } from 'ajv'
import { clone, isEqual, keys, setWith } from 'lodash'
import * as React from 'react'
import useHeightObserver from '../../../hooks/useHeightObserver'
import { addResizeObserverToElement } from '../../../utilities/resizeObserver'
import { getIsMobile, getMobileDevice, isWebview } from '../../../utilities/userAgent'
import FullHeightWrapper from '../../atoms/full-height-wrapper'
import SimpleSpacer from '../../atoms/simple-spacer'
import { StickyBottom } from '../../atoms/sticky-bottom'
import { SubmitOnboardingCallback } from '../../pages/onboarding-workflow/OnboardingWorkflow'
import { BackButton } from './BackButton'
import { FooterSection } from './FooterSection'
import { Section } from './Section'
import { Page, SectionProps, TemplateResponseData, ExtraData } from './types'
import { ValidationProvider } from './useValidation'
import { WorkflowEventTrackingProps } from '../../../events'

type WorkflowTemplateProps = {
  currentPageTemplate: Page
  onSubmitStep: SubmitOnboardingCallback
  trackWorkflowStepRendered: () => void
  trackWorkflowEvent: (trackingData: WorkflowEventTrackingProps) => void
}

// avoid unnecessary rendering when service returns the same template
const areTemplatesEqual = (preProp, nextProp) => {
  return (
    JSON.stringify(preProp.currentPageTemplate) ===
    JSON.stringify(nextProp.currentPageTemplate)
  )
}

export const WorkflowTemplate: React.FC<WorkflowTemplateProps> = React.memo(
  ({ currentPageTemplate, onSubmitStep, trackWorkflowStepRendered, trackWorkflowEvent }) => {
    const [templateResponseData, setTemplateResponseData] = React.useState<
      TemplateResponseData
    >(currentPageTemplate.template_response_data)

    const [extraData, setExtraData] = React.useState<ExtraData>(null)

    const {
      height: footerHeight,
      resizeObserver: footerResizeObserver,
    } = useHeightObserver()

    React.useEffect(() => {
      setTemplateResponseData(currentPageTemplate.template_response_data)
      trackWorkflowStepRendered()
    }, [currentPageTemplate, trackWorkflowStepRendered])

    React.useEffect(() => {
      setExtraData({
        os_type: getMobileDevice(),
        is_webview: isWebview()
      })
    }, [])

    const updateData = (path: string, value) => {
      setTemplateResponseData(currentData =>
        setWith(clone(currentData), path, value, clone)
      )
    }

    if (
      !isEqual(
        keys(templateResponseData),
        keys(currentPageTemplate.template_response_data)
      )
    ) {
      return <Loading />
    }

    const mainSections: SectionProps[] = currentPageTemplate.sections.filter(
      section => {
        return (
          !section.type ||
          (section.type !== 'FOOTER' && section.type !== 'HEADER')
        )
      }
    )

    const footerSection: SectionProps = currentPageTemplate.sections.filter(
      section => {
        return section.type === 'FOOTER'
      }
    )[0]

    const headerSections: SectionProps = currentPageTemplate.sections.filter(
      section => {
        return section.type === 'HEADER'
      }
    )[0]

    const isMobile = getIsMobile()
    const validationSchema =
      currentPageTemplate.validation_schema ||
      (({} as unknown) as JSONSchemaType<TemplateResponseData>)

    return (
      <ValidationProvider schema={validationSchema}>
        {headerSections && headerSections.components.length > 0 && (
          <Section
            section={headerSections}
            onDataUpdate={updateData}
            templateResponseData={templateResponseData}
            onSubmitStep={onSubmitStep}
            extraData={extraData}
            trackWorkflowEvent={trackWorkflowEvent}
          />
        )}
        <FullHeightWrapper isMobile={isMobile}>
          <Inset size={Inset.Sizes.Large}>
            <StackChildren size={StackChildren.Sizes.Large}>
              <StackChildren size={StackChildren.Sizes.XxSmall}>
                {currentPageTemplate.back_button_data && (
                  <BackButton
                    backButtonData={currentPageTemplate.back_button_data}
                    onSubmitStep={onSubmitStep}
                  />
                )}
                {currentPageTemplate.header && (
                  <Text styles={Text.Styles.PageTitle2}>
                    {currentPageTemplate.header}
                  </Text>
                )}
                {currentPageTemplate.sub_header && (
                  <Text styles={Text.Styles.Body2}>
                    {currentPageTemplate.sub_header}
                  </Text>
                )}
              </StackChildren>
              {mainSections.map((section: SectionProps, index: number) => (
                <div key={index}>
                  <Section
                    section={section}
                    onDataUpdate={updateData}
                    templateResponseData={templateResponseData}
                    onSubmitStep={onSubmitStep}
                    extraData={extraData}
                    trackWorkflowEvent={trackWorkflowEvent}
                  />
                  {!section.hide_separator &&
                    index !== mainSections.length - 1 && (
                      // TODO: add condition to remove separator between sections here
                      <Separator
                        type={Separator.Types.FullBorder}
                        separatorHeight={Spacing.Small}
                        verticalMargin={Spacing.Large}
                      />
                    )}
                </div>
              ))}
            </StackChildren>
          </Inset>
          {isMobile && <SimpleSpacer $height={footerHeight} />}
          {((footerSection && footerSection.components.length > 0) ||
            currentPageTemplate.cta_buttons.length > 0) && (
            <StickyBottom isMobile={isMobile}>
              <div ref={addResizeObserverToElement(footerResizeObserver)}>
                <FooterSection
                  section={footerSection}
                  onDataUpdate={updateData}
                  buttons={currentPageTemplate.cta_buttons}
                  templateResponseData={templateResponseData}
                  onSubmitStep={onSubmitStep}
                  trackWorkflowEvent={trackWorkflowEvent}
                  extraData={extraData}
                />
              </div>
            </StickyBottom>
          )}
        </FullHeightWrapper>
      </ValidationProvider>
    )
  },
  areTemplatesEqual
)

WorkflowTemplate.displayName = 'WorkflowTemplate'
